import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { baixarCSV } from '../utils';
import CartaoContagem from './CartaoContagem';
import FolhaRelatorio from './FolhaRelatorio';
import RelatorioCamisetas from './RelatorioCamisetas';
import type { PapelCamiseta, SituacaoCamiseta } from './RelatorioCamisetas';
import type {
  ContagemOpcao,
  ContagemPergunta,
  FiltroOpcao,
  LinhaRelatorio,
  SecaoEvento,
} from '../types';

interface Props {
  eventoId: number;
  eventoNome: string;
  // Inscrições da lista filtrada no momento (todas as páginas).
  // null = nenhum filtro ativo, ou seja, o evento inteiro.
  inscricaoIds?: number[] | null;
  resumoFiltro?: string;
  /** De quem é o relatório de camisetas (aba Equipe / Campistas). */
  papel?: PapelCamiseta | null;
  /** Situação das camisetas, vinda da aba da lista. */
  situacaoCamisetas?: SituacaoCamiseta;
  /** Lista filtrada já define a situação: não deixa trocar. */
  travarSituacaoCamisetas?: boolean;
  rotuloSituacaoCamisetas?: string;
  /** Ids da lista filtrada só para as camisetas (padrão: os mesmos dos relatórios). */
  camisetaIds?: number[] | null;
}

export default function AbaRelatorios({
  eventoId,
  eventoNome,
  inscricaoIds = null,
  resumoFiltro,
  papel = null,
  situacaoCamisetas = 'com_vaga',
  travarSituacaoCamisetas = false,
  rotuloSituacaoCamisetas,
  camisetaIds,
}: Props) {
  const [secoes, setSecoes] = useState<SecaoEvento[]>([]);
  const [contagens, setContagens] = useState<ContagemPergunta[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [secaoAberta, setSecaoAberta] = useState<string | null>(null);
  const [linhasFolha, setLinhasFolha] = useState<LinhaRelatorio[] | null>(null);
  const [gerando, setGerando] = useState(false);

  const rotuloFiltro = resumoFiltro || 'todos os inscritos';
  // Chave estável: evita recarregar a cada render (o array muda de identidade).
  const chaveIds = useMemo(
    () => (inscricaoIds ? `i:${inscricaoIds.join(',')}` : 'todos'),
    [inscricaoIds],
  );

  useEffect(() => {
    let ativo = true;
    const ids = inscricaoIds ?? null;
    async function carregar() {
      setCarregando(true);
      setErro(null);
      const [resSecoes, resFiltros] = await Promise.all([
        supabase.rpc('secoes_do_evento', { p_evento_id: eventoId, p_inscricoes: ids }),
        supabase.rpc('filtros_do_evento', { p_evento_id: eventoId, p_inscricoes: ids }),
      ]);
      if (!ativo) return;
      if (resSecoes.error) {
        setErro(mensagemAmigavel(resSecoes.error));
        setCarregando(false);
        return;
      }
      if (resFiltros.error) {
        setErro(mensagemAmigavel(resFiltros.error));
        setCarregando(false);
        return;
      }

      setSecoes((resSecoes.data as SecaoEvento[]) || []);

      // Perguntas de escolha (escolha_unica / multipla_escolha) viram cartões de números.
      const linhas = (resFiltros.data as FiltroOpcao[]) || [];
      const porId = new Map<number, ContagemPergunta>();
      linhas.forEach((l) => {
        if (l.tipo !== 'escolha_unica' && l.tipo !== 'multipla_escolha') return;
        if (!porId.has(l.pergunta_id)) {
          porId.set(l.pergunta_id, {
            pergunta_id: l.pergunta_id,
            enunciado: l.enunciado,
            secao: l.secao,
            opcoes: [],
          });
        }
      });
      const perguntasEscolha = [...porId.values()];

      const resultados = await Promise.all(
        perguntasEscolha.map(async (c) => {
          const { data } = await supabase.rpc('contagem_resposta', {
            p_evento_id: eventoId,
            p_pergunta_id: c.pergunta_id,
            p_inscricoes: ids,
          });
          return {
            pergunta: c,
            opcoes: ((data as ContagemOpcao[]) || []).map((o) => ({
              opcao: o.opcao,
              quantos: Number(o.quantos),
              percentual: Number(o.percentual),
            })),
          };
        }),
      );

      if (!ativo) return;
      setContagens(
        resultados.map((r) => ({ ...r.pergunta, opcoes: r.opcoes })),
      );
      setCarregando(false);
    }
    carregar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId, chaveIds]);

  async function aoGerar(secao: string) {
    setGerando(true);
    setErro(null);
    try {
      const { data, error } = await supabase.rpc('relatorio_secao', {
        p_evento_id: eventoId,
        p_secao: secao,
        p_inscricoes: inscricaoIds ?? null,
      });
      if (error) throw error;
      setLinhasFolha((data as LinhaRelatorio[]) || []);
      setSecaoAberta(secao);
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível gerar o relatório.'));
    } finally {
      setGerando(false);
    }
  }

  function aoBaixarContagem(pergunta: ContagemPergunta) {
    const cabecalho = ['Opção', 'Quantos', 'Percentual'];
    const linhas = pergunta.opcoes.map((o) => [
      o.opcao,
      String(o.quantos),
      `${o.percentual.toLocaleString('pt-BR')}%`,
    ]);
    const slug = pergunta.enunciado.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
    baixarCSV(`contagem-${slug}.csv`, [[`Filtro: ${rotuloFiltro}`], [], cabecalho, ...linhas]);
  }

  if (carregando) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-44 animate-pulse rounded-lg bg-secondary-100" />
        ))}
      </div>
    );
  }

  if (erro && !secaoAberta) {
    return (
      <div className="rounded-lg border border-secondary-200 bg-white p-10 text-center">
        <p className="text-[15px] text-foreground-800">{erro}</p>
        <p className="mt-1 text-sm text-secondary-500">Tente recarregar a página.</p>
      </div>
    );
  }

  if (secaoAberta && linhasFolha) {
    return (
      <FolhaRelatorio
        eventoNome={eventoNome}
        secao={secaoAberta}
        linhas={linhasFolha}
        resumoFiltro={rotuloFiltro}
        aoVoltar={() => {
          setSecaoAberta(null);
          setLinhasFolha(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-base font-medium text-foreground-950">Números da ficha</h2>
        <p className="mt-0.5 text-sm text-secondary-500">
          A contagem de cada pergunta de escolha. Baixe o CSV de cada uma para enviar à loja de
          camisetas, à cozinha ou a quem precisar.
        </p>
        {contagens.length === 0 ? (
          <p className="mt-4 rounded-lg border border-secondary-200 bg-white p-6 text-sm text-secondary-500">
            Esta ficha não tem perguntas de escolha.
          </p>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {contagens.map((c) => (
              <CartaoContagem key={c.pergunta_id} pergunta={c} aoBaixar={aoBaixarContagem} />
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-base font-medium text-foreground-950">Folhas por bloco</h2>
        <p className="mt-0.5 text-sm text-secondary-500">
          Gere uma folha pronta para a equipe levar na mão, com uma coluna por pergunta do bloco.
        </p>
        {secoes.length === 0 ? (
          <p className="mt-4 rounded-lg border border-secondary-200 bg-white p-6 text-sm text-secondary-500">
            Esta ficha não tem blocos para gerar relatórios.
          </p>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {secoes.map((s) => (
              <div
                key={s.secao}
                className="flex flex-col rounded-lg border border-secondary-200 bg-white p-4"
              >
                <h3 className="text-sm font-medium text-foreground-950">{s.secao}</h3>
                <p className="mt-1 text-sm text-secondary-500">
                  {Number(s.perguntas)} {Number(s.perguntas) === 1 ? 'pergunta' : 'perguntas'}
                </p>
                <button
                  type="button"
                  onClick={() => aoGerar(s.secao)}
                  disabled={gerando}
                  className="mt-3 flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className="ri-file-list-3-line text-base" />
                  {gerando ? 'Gerando…' : 'Gerar'}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-base font-medium text-foreground-950">Camisetas</h2>
        <p className="mt-0.5 text-sm text-secondary-500">
          Quantas peças pedir em cada tamanho, por situação da inscrição.
        </p>
        <div className="mt-4">
          <RelatorioCamisetas
            eventoId={eventoId}
            eventoNome={eventoNome}
            papel={papel}
            situacaoInicial={situacaoCamisetas}
            travarSituacao={travarSituacaoCamisetas}
            rotuloSituacao={rotuloSituacaoCamisetas}
            inscricaoIds={camisetaIds !== undefined ? camisetaIds : inscricaoIds}
          />
        </div>
      </section>
    </div>
  );
}